<script lang="ts" module>
  import type { HTMLActionArray } from "#lib"
  import type { BaseProps } from "#lib/internal/types"
  import type { VariantProps } from "class-variance-authority"
  import type { ClassValue } from "class-variance-authority/types"
  import type { Snippet } from "svelte"
  import type {
    HTMLAnchorAttributes,
    HTMLButtonAttributes,
  } from "svelte/elements"
  import { buttonVariants } from "."

  export type ButtonProps = Omit<
    BaseProps<"button"> & BaseProps<"a">,
    "class"
  > & {
    class?: ClassValue | null
    href?: HTMLAnchorAttributes["href"]
    type?: HTMLButtonAttributes["type"]
    variant?: VariantProps<typeof buttonVariants>["variant"]
    size?: VariantProps<typeof buttonVariants>["size"]
    use?: HTMLActionArray
    ref?: HTMLAnchorElement | HTMLButtonElement | null
    target?: HTMLAnchorAttributes["target"]
    children?: Snippet
  }
</script>

<script lang="ts">
  import { getUiclContext, useActions } from "#lib"
  import { cn } from "#lib/utils"

  let {
    class: className = undefined,
    href = undefined,
    type = "button",
    variant = "default",
    size = undefined,
    ref = $bindable(null),
    target = undefined,
    use = [],
    children,
    ...restProps
  }: ButtonProps = $props()

  const resolvedTarget = $derived(
    target !== undefined ? target : href ? "_blank" : undefined
  )

  const v2 = !!getUiclContext()?.v2
</script>

<svelte:element
  this={href ? "a" : "button"}
  type={href ? undefined : type}
  {href}
  class={cn(buttonVariants({ variant, size, v2, className }))}
  use:useActions={use}
  {...restProps}
  role={href ? "link" : "button"}
  bind:this={ref}
  target={resolvedTarget}
  data-variant={variant}
>
  {@render children?.()}
</svelte:element>

<style lang="postcss">
  .v2-transitions {
    transition-property: background-color, transform, translate, scale, rotate;
    transition-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  }
</style>
